<!-- HorizontalScrollContainer.vue -->
<template>
  <div class="h-scroll">
    <!-- 左滚动按钮 -->
    <tx-btn
      src="oldclassic_p_rank_fanye-zuo"
      v-show="showLeftButton || showRightButton"
      @click="scrollLeft"
      @mousedown="startContinuousScroll('left')"
      @mouseup="stopContinuousScroll"
      @mouseleave="stopContinuousScroll"
      name="左滚动"
    />

    <!-- 滚动内容区域 -->
    <div
      ref="scrollContainer"
      class="scroll-content"
      :style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
      @scroll="handleScroll"
      @mousedown="startDrag"
      @mousemove="onDrag"
      @mouseup="stopDrag"
      @mouseleave="stopDrag"
      @wheel="handleWheel"
    >
      <div class="scroll-inner" ref="scrollInner">
        <slot></slot>
      </div>
    </div>

    <!-- 右滚动按钮 -->
    <tx-btn
      src="oldclassic_p_rank_fanye-you"
      v-show="showLeftButton || showRightButton"
      @click="scrollRight"
      @mousedown="startContinuousScroll('right')"
      @mouseup="stopContinuousScroll"
      @mouseleave="stopContinuousScroll"
      name="右滚动"
    />
  </div>
</template>

<script setup>
const props = defineProps({
  scrollStep: {
    // 滚动步长
    type: Number,
    default: 200,
  },

  autoHide: {
    // 是否自动隐藏按钮
    type: Boolean,
    default: true,
  },
  scrollDuration: {
    // 滚动动画持续时间
    type: Number,
    default: 300,
  },
  enableDrag: {
    // 是否启用鼠标拖拽
    type: Boolean,
    default: true,
  },
  enableWheel: {
    // 是否启用鼠标滚轮
    type: Boolean,
    default: true,
  },
  continuousScrollInterval: {
    // 连续滚动间隔
    type: Number,
    default: 100,
  },
});

// Emits定义
const emit = defineEmits(['scroll', 'scroll-start', 'scroll-end']);

// 响应式数据
const scrollContainer = ref(null);
const scrollInner = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const showControls = ref(true);
const isDragging = ref(false);
const dragStart = ref({ x: 0, scrollLeft: 0 });
const continuousScrollTimer = ref(null);

// 计算属性
const showLeftButton = computed(() => {
  // return showControls.value && canScrollLeft.value
  return showControls.value && canScrollLeft.value;
});

const showRightButton = computed(() => {
  return showControls.value && canScrollRight.value;
});

// 检查滚动状态
const checkScrollState = () => {
  if (!scrollContainer.value) return;

  const container = scrollContainer.value;
  const scrollLeft = container.scrollLeft;
  const maxScrollLeft = container.scrollWidth - container.clientWidth;

  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < maxScrollLeft;

  // 发送滚动事件
  emit('scroll', {
    scrollLeft,
    maxScrollLeft,
    canScrollLeft: canScrollLeft.value,
    canScrollRight: canScrollRight.value,
    scrollPercentage:
      maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * 100 : 0,
  });
};

// 平滑滚动函数
const smoothScroll = (targetScrollLeft) => {
  if (!scrollContainer.value) return;

  const container = scrollContainer.value;
  const startScrollLeft = container.scrollLeft;
  const distance = targetScrollLeft - startScrollLeft;
  const startTime = performance.now();

  emit('scroll-start');

  const animateScroll = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / props.scrollDuration, 1);

    // 使用easeOutCubic缓动函数
    const easeProgress = 1 - Math.pow(1 - progress, 3);

    container.scrollLeft = startScrollLeft + distance * easeProgress;

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    } else {
      emit('scroll-end');
    }
  };

  requestAnimationFrame(animateScroll);
};

// 滚动方法
const scrollLeft = () => {
  if (!canScrollLeft.value) return;

  const container = scrollContainer.value;
  const targetScrollLeft = Math.max(0, container.scrollLeft - props.scrollStep);
  smoothScroll(targetScrollLeft);
};

const scrollRight = () => {
  if (!canScrollRight.value) return;

  const container = scrollContainer.value;
  const maxScrollLeft = container.scrollWidth - container.clientWidth;
  const targetScrollLeft = Math.min(
    maxScrollLeft,
    container.scrollLeft + props.scrollStep
  );
  smoothScroll(targetScrollLeft);
};

// 连续滚动
const startContinuousScroll = (direction) => {
  const scrollFunc = direction === 'left' ? scrollLeft : scrollRight;

  // 立即执行一次
  scrollFunc();

  // 设置连续滚动
  continuousScrollTimer.value = setInterval(() => {
    scrollFunc();
  }, props.continuousScrollInterval);
};

const stopContinuousScroll = () => {
  if (continuousScrollTimer.value) {
    clearInterval(continuousScrollTimer.value);
    continuousScrollTimer.value = null;
  }
};

// 鼠标拖拽
const startDrag = (e) => {
  if (!props.enableDrag) return;

  e.preventDefault();
  isDragging.value = true;

  dragStart.value = {
    x: e.clientX,
    scrollLeft: scrollContainer.value.scrollLeft,
  };

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (e) => {
  if (!isDragging.value) return;

  e.preventDefault();
  const deltaX = e.clientX - dragStart.value.x;
  scrollContainer.value.scrollLeft = dragStart.value.scrollLeft - deltaX;
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

// 鼠标滚轮
const handleWheel = (e) => {
  if (!props.enableWheel) return;

  // 如果是水平滚动，直接处理
  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
    return;
  }

  // 将垂直滚动转换为水平滚动
  e.preventDefault();
  scrollContainer.value.scrollLeft += e.deltaY;
};

// 滚动事件处理
const handleScroll = () => {
  checkScrollState();
};

// 公开方法
const scrollToPosition = (position) => {
  smoothScroll(position);
};

const scrollToElement = (element) => {
  if (!scrollContainer.value) return;

  const container = scrollContainer.value;
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const targetScrollLeft =
    container.scrollLeft + elementRect.left - containerRect.left;

  smoothScroll(targetScrollLeft);
};

// 响应式监听
const resizeObserver = ref(null);

// 生命周期
onMounted(async () => {
  await nextTick();
  checkScrollState();

  // 监听容器大小变化
  if (window.ResizeObserver) {
    resizeObserver.value = new ResizeObserver(() => {
      checkScrollState();
    });

    if (scrollContainer.value) {
      resizeObserver.value.observe(scrollContainer.value);
    }
    if (scrollInner.value) {
      resizeObserver.value.observe(scrollInner.value);
    }
  }

  // 监听窗口大小变化
  window.addEventListener('resize', checkScrollState);
});

onUnmounted(() => {
  stopContinuousScroll();

  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }

  window.removeEventListener('resize', checkScrollState);
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
});

// 暴露方法给父组件
defineExpose({
  scrollLeft,
  scrollRight,
  scrollToPosition,
  scrollToElement,
  checkScrollState,
});
</script>

<style scoped>
.h-scroll {
  /* position: relative; */
  display: flex;
  align-items: center;
  overflow: hidden;
}

.scroll-content {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
}

/* .scroll-content::-webkit-scrollbar {
  display: none;
} */

.scroll-inner {
  display: flex;
  min-width: max-content;
  gap: 10px;
}
</style>